// src/pages/nascar/F1Optimizer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));
const num = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).replace(/\$/g, "").replace(/[, %]/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};
const fmt0 = (n) => (Number.isFinite(+n) ? (+n).toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(+n) ? (+n).toFixed(1) : "—");

// Display only; full driver names remain in lineup state and CSV exports.
const shortDriverName = (name) => {
  const parts = String(name || "").trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0][0]}. ${parts.slice(1).join(" ")}` : parts[0] || "";
};

const truncateName = (s, max = 16) => {
  const v = String(s || "");
  return v.length > max ? `${v.slice(0, max).trim()}…` : v;
};

const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

const geoMeanPct = (vals) => {
  const arr = (vals || []).map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
  if (!arr.length) return 0;
  const logAvg = arr.reduce((sum, v) => sum + Math.log(v), 0) / arr.length;
  return Math.exp(logAvg);
};

const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z0-9' -]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const makeRowKey = (type, name, slot) => `${type}::${name}::${slot}`;
const siteIdKey = (name, slot) => `${normName(name)}::${slot}`;

function probToPct(prob) {
  const p = num(prob);
  return Number.isFinite(p) ? p * 100 : 0;
}

function downloadBlob(filename, content, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    fetch(url, { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("application/json")) return r.json();
        return JSON.parse(await r.text());
      })
      .then((j) => {
        if (!alive) return;
        setData(j);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e);
        setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err, loading };
}

function useLastUpdated(metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;

    fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((meta) => {
        if (!alive) return;
        const iso =
          meta?.updated_iso ||
          meta?.updated_utc ||
          meta?.updated ||
          meta?.lastUpdated ||
          null;
        if (iso) {
          const d = new Date(iso);
          if (!isNaN(d)) setUpdatedAt(d);
        }
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, [metaUrl]);

  return updatedAt;
}

const useStickyState = (key, init) => {
  const getInit = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch (_) {}
    return typeof init === "function" ? init() : init;
  }, [key, init]);

  const [v, setV] = React.useState(() => getInit());

  React.useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch (_) {}
  }, [key, v]);

  React.useEffect(() => {
    setV(getInit());
  }, [getInit]);

  return [v, setV];
};

function DriverMultiPicker({ allNames, value, onChange, placeholder = "Add names…", isDark }) {
  const [q, setQ] = useState("");
  const selected = useMemo(() => new Set(value || []), [value]);

  const suggestions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return allNames.filter((n) => !selected.has(n) && n.toLowerCase().includes(needle)).slice(0, 12);
  }, [q, allNames, selected]);

  const add = (name) => {
    if (!selected.has(name)) onChange([...(value || []), name]);
    setQ("");
  };

  const remove = (name) => onChange((value || []).filter((v) => v !== name));

  return (
    <div className="w-full">
      <div
        className={
          "flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 border " +
          (isDark
            ? "bg-slate-900/80 border-slate-700 text-slate-100"
            : "bg-white border-slate-300 text-slate-800")
        }
      >
        {(value || []).map((name) => (
          <span
            key={name}
            className={
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] border " +
              (isDark
                ? "bg-slate-800/90 border-slate-600 text-slate-100"
                : "bg-blue-50 border-blue-200 text-blue-800")
            }
          >
            {name}
            <button
              type="button"
              onClick={() => remove(name)}
              className={isDark ? "text-slate-300 hover:text-white" : "text-blue-700 hover:text-blue-900"}
            >
              ×
            </button>
          </span>
        ))}

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && suggestions.length) {
              e.preventDefault();
              add(suggestions[0]);
            }
          }}
          className="flex-1 min-w-[160px] outline-none text-sm py-1 bg-transparent"
          placeholder={placeholder}
        />
      </div>

      {!!q && suggestions.length > 0 && (
        <div
          className={
            "mt-1 max-h-48 overflow-auto rounded-md border shadow-sm " +
            (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200")
          }
        >
          {suggestions.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => add(n)}
              className={
                "block w-full text-left px-3 py-1.5 text-sm " +
                (isDark ? "text-slate-100 hover:bg-slate-800/80" : "text-slate-900 hover:bg-blue-50")
              }
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- theme ----------------------------- */
const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-white shadow-md",
  pill: "bg-white ring-slate-300",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-500 hover:text-slate-800",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-500 hover:text-slate-800",
  input: "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
  card: "bg-gradient-to-b from-slate-950 via-slate-950 to-slate-950/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]",
  pill: "bg-slate-900/90 ring-slate-700/80",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-400 hover:text-slate-100",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-500 hover:text-slate-200",
  input: "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500",
};

const TEAM_COLORS = {
  MERC: "#4CC7B8",
  FERR: "#DC2626",
  MCL: "#F59E0B",
  RB: "#4F46E5",
  HAAS: "#9CA3AF",
  ALPN: "#F472B6",
  VCARB: "#38BDF8",
  AUD: "#10B981",
  WILL: "#3B82F6",
  AM: "#22C55E",
  CAD: "#A855F7",
};

const teamPillClass = (team) => {
  const bg = TEAM_COLORS[team] || "#475569";
  return { backgroundColor: bg, color: "#ffffff" };
};

const F1 = {
  key: "f1",
  label: "Formula 1",
  drivers: "/data/f1/latest/f1_driver_sims.json",
  constructors: "/data/f1/latest/f1_constructor_sims.json",
  siteIds: "/data/f1/latest/site_ids.json",
  meta: "/data/f1/latest/meta.json",
  route: "/nascar/misc/f1/optimizer",
  cap: 50000,
  capTag: "CPT",
};

/* -------------------------- local solver -------------------------- */
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function uniqueLineupKey(L) {
  return `${L.cpt}||${(L.flex || []).slice().sort().join("|")}||${L.constructor}`;
}

function passesGroups(lineupNames, groups) {
  for (const g of groups || []) {
    const players = Array.isArray(g.players) ? g.players : [];
    const count = players.filter((p) => lineupNames.has(p)).length;
    const target = Math.max(0, Number(g.count) || 0);
    const mode = g.mode || "at_most";
    if (mode === "at_most" && count > target) return false;
    if (mode === "at_least" && count < target) return false;
    if (mode === "exactly" && count !== target) return false;
  }
  return true;
}

function localBuildF1Lineups({
  drivers,
  constructors,
  objective,
  n,
  cap,
  randomnessPct,
  locks,
  excludes,
  boostMap,
  minPctTag,
  maxPctTag,
  globalMax,
  lineupPownCap,
  geoMeanPownCap,
  groups,
}) {
  const want = Math.max(1, Number(n) || 1);
  const made = [];
  const seen = new Set();
  const counts = new Map();

  const randomScale = clamp(Number(randomnessPct) || 0, 0, 100) / 100;
  const maxGlobal = clamp(Number(globalMax) || 100, 0, 100);

  const currentPct = (key) => (made.length ? ((counts.get(key) || 0) / made.length) * 100 : 0);

  const maxAllowed = (key) => {
    const byRow = maxPctTag?.[key];
    if (String(byRow ?? "").trim() !== "") return clamp(Number(byRow) || 0, 0, 100);
    return maxGlobal;
  };

  const minNeeded = (key) => {
    const byRow = minPctTag?.[key];
    return String(byRow ?? "").trim() !== "" ? clamp(Number(byRow) || 0, 0, 100) : 0;
  };

  const getMetric = (row) => {
    if (objective === "floor") return row.floorDisplay || 0;
    if (objective === "ceil") return row.ceilDisplay || 0;
    if (objective === "pown") return row.pownDisplay || 0;
    if (objective === "opt") return row.optDisplay || 0;
    return row.projDisplay || 0;
  };

  const score = (row) => {
    const base = getMetric(row);
    const boost = (boostMap?.[row.name] || 0) * 0.03;
    const rand = randomScale > 0 ? (Math.random() * 2 - 1) * randomScale * Math.max(1, base) * 0.25 : 0;
    return base * (1 + boost) + rand;
  };

  const availableCPT = drivers
    .filter((r) => r.slot === "CPT")
    .filter((r) => !excludes.has(r.key))
    .sort((a, b) => score(b) - score(a));

  const availableFlex = drivers
    .filter((r) => r.slot === "FLEX")
    .filter((r) => !excludes.has(r.key))
    .sort((a, b) => score(b) - score(a));

  const availableCnstr = constructors
    .filter((r) => !excludes.has(r.key))
    .sort((a, b) => score(b) - score(a));

  const lockedCPT = availableCPT.filter((r) => locks.has(r.key));
  const lockedFlex = availableFlex.filter((r) => locks.has(r.key));
  const lockedCnstr = availableCnstr.filter((r) => locks.has(r.key));

  const attempts = want * 500;

  for (let attempt = 0; attempt < attempts && made.length < want; attempt += 1) {
    let cpt = null;
    let cnstr = null;
    let flex = [];

    if (lockedCPT.length > 1) continue;
    if (lockedCnstr.length > 1) continue;
    if (lockedFlex.length > 4) continue;

    cpt = lockedCPT.length ? lockedCPT[0] : shuffle(availableCPT.slice(0, Math.min(12, availableCPT.length)))[0];
    if (!cpt) continue;

    cnstr = lockedCnstr.length ? lockedCnstr[0] : shuffle(availableCnstr.slice(0, Math.min(10, availableCnstr.length)))[0];
    if (!cnstr) continue;

    flex = lockedFlex.slice();
    const usedNames = new Set([cpt.name, ...flex.map((r) => r.name)]);

    const flexPool = shuffle(availableFlex).filter((r) => !usedNames.has(r.name));

    for (const cand of flexPool) {
      if (flex.length >= 4) break;
      usedNames.add(cand.name);
      flex.push(cand);
    }
    if (flex.length !== 4) continue;

    // Constructor team rule:
    // cannot have constructor + 2 drivers from that same team
    const sameTeamDriverCount = [cpt, ...flex].filter((r) => r.team === cnstr.team).length;
    if (sameTeamDriverCount > 1) continue;

    const chosen = [cpt, ...flex, cnstr];
    const salary = chosen.reduce((s, r) => s + (r.salaryDisplay || 0), 0);
    if (salary > cap) continue;

    const lineupPown = chosen.reduce((s, r) => s + (r.pownDisplay || 0), 0);
    const geoPown = geoMeanPct(chosen.map((r) => r.pownDisplay || 0));
    if (lineupPownCap != null && lineupPown > lineupPownCap) continue;
    if (geoMeanPownCap != null && geoPown > geoMeanPownCap) continue;

    const lineupNames = new Set([cpt.name, ...flex.map((r) => r.name), cnstr.name]);
    if (!passesGroups(lineupNames, groups)) continue;

    // exposure caps
    let overCap = false;
    for (const r of chosen) {
      if (currentPct(r.key) >= maxAllowed(r.key)) {
        overCap = true;
        break;
      }
    }
    if (overCap) continue;

    const total = chosen.reduce((s, r) => s + (getMetric(r) || 0), 0);
    const L = {
      cpt: cpt.name,
      flex: flex.map((r) => r.name),
      constructor: cnstr.name,
      salary,
      total,
      pairs: [
        { slot: "CPT", type: "DRIVER", name: cpt.name },
        ...flex.map((n) => ({ slot: "FLEX", type: "DRIVER", name: n })),
        { slot: "CNSTR", type: "CONSTRUCTOR", name: cnstr.name },
      ],
    };

    const key = uniqueLineupKey(L);
    if (seen.has(key)) continue;

    seen.add(key);
    made.push(L);

    for (const r of chosen) counts.set(r.key, (counts.get(r.key) || 0) + 1);
  }

  // very light min% repair
  if (made.length) {
    const mustHave = [...drivers, ...constructors].filter((r) => minNeeded(r.key) > 0);
    for (const r of mustHave) {
      const needPct = minNeeded(r.key);
      const current = ((counts.get(r.key) || 0) / made.length) * 100;
      if (current >= needPct) continue;

      // No hard repair rewrite here; just leave result as-is.
      // Frontend fallback is meant to be usable, not perfect.
    }
  }

  return made;
}

/* ------------------------------ export ------------------------------ */
function downloadDKEntriesCSV({ lineups, driverSiteIds, constructorSiteIds, fname = "f1_dk_entries.csv" }) {
  if (!lineups?.length) return;

  const byNameAndSlot = new Map();
  const constructorIndex = new Map();

  for (const r of driverSiteIds || []) {
    const nameKey = normName(r.name || r.display);
    const slot = String(r.roster_position || "").toUpperCase();
    if (!nameKey || !slot) continue;
    byNameAndSlot.set(`${nameKey}::${slot}`, r);
  }

  for (const r of constructorSiteIds || []) {
    const nameKey = normName(r.name || r.display);
    if (!nameKey) continue;
    constructorIndex.set(nameKey, r);
  }

  const header = ["CPT", "D", "D", "D", "D", "CNSTR"].join(",");

  const lines = (lineups || []).map((L) => {
    const cptRec = byNameAndSlot.get(`${normName(L.cpt)}::CPT`);
    const flexRecs = (L.flex || []).map((name) => byNameAndSlot.get(`${normName(name)}::D`));
    const cnRec = constructorIndex.get(normName(L.constructor));

    const cells = [
      cptRec ? `${cptRec.display} (${cptRec.id})` : L.cpt || "",
      ...flexRecs.map((r, i) => (r ? `${r.display} (${r.id})` : L.flex?.[i] || "")),
      cnRec ? `${cnRec.display} (${cnRec.id})` : L.constructor || "",
    ];

    while (cells.length < 6) cells.push("");

    return cells.map(escapeCSV).join(",");
  });

  downloadBlob(fname, [header, ...lines].join("\n"));
}

function downloadExposuresCSV(rows, fname = "f1_exposures.csv") {
  if (!rows?.length) return;
  const header = ["Name", "Slot", "Count", "Exposure %"].join(",");
  const body = rows.map((r) => [escapeCSV(r.name), escapeCSV(r.slot), r.count, r.pct.toFixed(1)].join(","));
  downloadBlob(fname, [header, ...body].join("\n"));
}

/* ============================== page =============================== */
export default function F1Optimizer() {
  const { data: driverData, err: driverErr, loading: driverLoading } = useJson(F1.drivers);
  const { data: constructorData, err: constructorErr, loading: constructorLoading } = useJson(F1.constructors);
  const { data: siteIdsData, err: siteIdsErr } = useJson(F1.siteIds);
  const updatedAt = useLastUpdated(F1.meta);

  const keyPrefix = "f1Opt.dk";
  const [theme, setTheme] = useStickyState(`${keyPrefix}.theme`, "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;
  const panelClass = isDark
    ? "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "border border-slate-200 bg-slate-50 shadow-sm";
  const actionBtnClass = "h-8 px-3 rounded-md border text-xs font-medium inline-flex items-center justify-center transition " +
    (isDark ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50");
  const plainTabClass = (active) => "pb-1.5 border-b-2 text-xs font-semibold transition " +
    (active ? "border-sky-400 text-sky-500" : isDark ? "border-transparent text-slate-500 hover:text-slate-100" : "border-transparent text-slate-500 hover:text-slate-900");


  const [optBy, setOptBy] = useStickyState(`${keyPrefix}.optBy`, "proj");
  const [numLineups, setNumLineups] = useStickyState(`${keyPrefix}.N`, 20);
  const [maxSalary, setMaxSalary] = useStickyState(`${keyPrefix}.cap`, F1.cap);
  const [globalMax, setGlobalMax] = useStickyState(`${keyPrefix}.gmax`, 100);
  const [maxLineupPown, setMaxLineupPown] = useStickyState(`${keyPrefix}.maxLineupPown`, "");
  const [maxGeoPown, setMaxGeoPown] = useStickyState(`${keyPrefix}.maxGeoPown`, "");
  const [randomness, setRandomness] = useStickyState(`${keyPrefix}.rand`, 0);

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useStickyState(`${keyPrefix}.typeFilter`, "ALL"); // ALL | DRIVER | CONSTRUCTOR
  const [slotFilter, setSlotFilter] = useStickyState(`${keyPrefix}.slotFilter`, "ALL"); // ALL | CPT | FLEX | CNSTR
  const [expScope, setExpScope] = useStickyState(`${keyPrefix}.expScope`, "ALL"); // ALL | CPT | FLEX | CNSTR

  const [locksArr, setLocksArr] = useStickyState(`${keyPrefix}.locks`, []);
  const [exclsArr, setExclsArr] = useStickyState(`${keyPrefix}.excls`, []);
  const [boost, setBoost] = useStickyState(`${keyPrefix}.boost`, {});
  const [customProj, setCustomProj] = useStickyState(`${keyPrefix}.customProj`, {});
  const [customProjDraft, setCustomProjDraft] = useState({});
  const [minPctTag, setMinPctTag] = useStickyState(`${keyPrefix}.minPctTag`, {});
  const [maxPctTag, setMaxPctTag] = useStickyState(`${keyPrefix}.maxPctTag`, {});
  const [groups, setGroups] = useStickyState(`${keyPrefix}.groups`, []);
  const [cardsSortBy, setCardsSortBy] = useStickyState(`${keyPrefix}.cardsSortBy`, "proj");
  const [cardsSortDir, setCardsSortDir] = useStickyState(`${keyPrefix}.cardsSortDir`, "desc");

  const locks = useMemo(() => new Set(Array.isArray(locksArr) ? locksArr : []), [locksArr]);
  const excls = useMemo(() => new Set(Array.isArray(exclsArr) ? exclsArr : []), [exclsArr]);

  const [lineups, setLineups] = useState([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [progressActual, setProgressActual] = useState(0);
  const [progressUI, setProgressUI] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!isOptimizing) return;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setProgressUI((p) => {
        const N = Math.max(1, Number(numLineups) || 1);
        const target = progressActual;
        const next = Math.min(Math.max(p + 1, target), N);
        return next;
      });
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [isOptimizing, progressActual, numLineups]);

  const loading = driverLoading || constructorLoading;
  const error = driverErr || constructorErr || siteIdsErr;

  const fmtUpdated = (d) =>
    d
      ? d.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })
      : null;

  const baseDrivers = useMemo(() => {
    const arr = Array.isArray(driverData) ? driverData : Array.isArray(driverData?.drivers) ? driverData.drivers : [];
    return arr
      .map((r) => {
        const name = String(r?.Name || "").trim();
        const team = String(r?.TeamAbbrev || "").trim().toUpperCase();
        const start = num(r?.StartPos || r?.Start || r?.GridPos || 0);
        const salary = num(r?.Salary || 0);
        const proj = num(r?.Driver_DK_Proj || r?.["DK Proj"] || r?.Proj || 0);
        const floor = num(r?.Driver_DK_Floor || r?.["DK Floor"] || r?.Floor || 0);
        const ceil = num(r?.Driver_DK_Ceiling || r?.["DK Ceiling"] || r?.Ceiling || 0);
        const pown = probToPct(r?.["DK pOWN%"] ?? r?.["pOWN%"] ?? r?.pOWN ?? 0);
        const opt = probToPct(r?.["DK Opt%"] ?? r?.["Opt%"] ?? r?.opt ?? 0);
        const val = salary > 0 ? (proj / salary) * 1000 : 0;
        return {
          type: "DRIVER",
          name,
          team,
          start,
          salary,
          proj,
          floor,
          ceil,
          pown,
          opt,
          val,
        };
      })
      .filter((r) => r.name && r.salary > 0);
  }, [driverData]);

  const baseConstructors = useMemo(() => {
    const arr = Array.isArray(constructorData)
      ? constructorData
      : Array.isArray(constructorData?.constructors)
      ? constructorData.constructors
      : [];
    return arr
      .map((r) => {
        const name = String(r?.Name || "").trim();
        const team = String(r?.TeamAbbrev || "").trim().toUpperCase();
        const salary = num(r?.Salary || 0);
        const proj = num(r?.["DK Proj"] || r?.Proj || 0);
        const floor = num(r?.["DK Floor"] || r?.Floor || proj * 0.8);
        const ceil = num(r?.["DK Ceiling"] || r?.Ceiling || proj * 1.2);
        const pown = probToPct(r?.["DK pOWN%"] ?? r?.["pOWN%"] ?? r?.pOWN ?? 0);
        const opt = probToPct(r?.["DK Opt%"] ?? r?.["Opt%"] ?? r?.opt ?? 0);
        const val = salary > 0 ? (proj / salary) * 1000 : 0;
        return {
          type: "CONSTRUCTOR",
          name,
          team,
          start: 0,
          salary,
          proj,
          floor,
          ceil,
          pown,
          opt,
          val,
        };
      })
      .filter((r) => r.name && r.salary > 0);
  }, [constructorData]);

  const driverRows = useMemo(() => {
    const out = [];
    const applyCustomProjection = (row) => {
      const rawCustom = customProj?.[row.key];
      const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
      const baseProjDisplay = Number(row.baseProjDisplay ?? row.projDisplay ?? 0) || 0;
      const projDisplay = hasCustomProj ? Math.max(0, num(rawCustom)) : baseProjDisplay;
      const salaryDisplay = Number(row.salaryDisplay || 0);
      const baseValDisplay = salaryDisplay > 0 ? (baseProjDisplay / salaryDisplay) * 1000 : 0;
      const valDisplay = salaryDisplay > 0 ? (projDisplay / salaryDisplay) * 1000 : 0;

      return {
        ...row,
        baseProjDisplay,
        projDisplay,
        baseValDisplay,
        valDisplay,
        customProj: rawCustom ?? "",
        hasCustomProj,
      };
    };

    for (const d of baseDrivers) {
      const cptSalary = Math.round(d.salary * 1.5);
      out.push(applyCustomProjection({
        ...d,
        type: "DRIVER",
        slot: "CPT",
        salaryDisplay: cptSalary,
        baseProjDisplay: d.proj * 1.5,
        projDisplay: d.proj * 1.5,
        floorDisplay: d.floor * 1.5,
        ceilDisplay: d.ceil * 1.5,
        pownDisplay: d.pown,
        optDisplay: d.opt,
        valDisplay: (d.proj * 1.5 / (cptSalary || 1)) * 1000,
        key: makeRowKey("DRIVER", d.name, "CPT"),
      }));

      out.push(applyCustomProjection({
        ...d,
        type: "DRIVER",
        slot: "FLEX",
        salaryDisplay: d.salary,
        baseProjDisplay: d.proj,
        projDisplay: d.proj,
        floorDisplay: d.floor,
        ceilDisplay: d.ceil,
        pownDisplay: d.pown,
        optDisplay: d.opt,
        valDisplay: d.val,
        key: makeRowKey("DRIVER", d.name, "FLEX"),
      }));
    }
    return out;
  }, [baseDrivers, customProj]);

  const constructorRows = useMemo(() => {
    return baseConstructors.map((c) => {
      const key = makeRowKey("CONSTRUCTOR", c.name, "CNSTR");
      const rawCustom = customProj?.[key];
      const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
      const baseProjDisplay = c.proj;
      const projDisplay = hasCustomProj ? Math.max(0, num(rawCustom)) : baseProjDisplay;
      const baseValDisplay = c.salary > 0 ? (baseProjDisplay / c.salary) * 1000 : 0;
      const valDisplay = c.salary > 0 ? (projDisplay / c.salary) * 1000 : 0;

      return {
        ...c,
        slot: "CNSTR",
        salaryDisplay: c.salary,
        baseProjDisplay,
        projDisplay,
        floorDisplay: c.floor,
        ceilDisplay: c.ceil,
        pownDisplay: c.pown,
        optDisplay: c.opt,
        baseValDisplay,
        valDisplay,
        customProj: rawCustom ?? "",
        hasCustomProj,
        key,
      };
    });
  }, [baseConstructors, customProj]);

  const expandedRows = useMemo(() => [...driverRows, ...constructorRows], [driverRows, constructorRows]);
  const allSelectableNames = useMemo(() => expandedRows.map((r) => r.name).filter(Boolean).sort((a, b) => a.localeCompare(b)), [expandedRows]);

  const rowsBySlotAndName = useMemo(() => {
    const m = new Map();
    for (const r of expandedRows) {
      m.set(`${r.slot}::${r.name}`, r);
    }
    return m;
  }, [expandedRows]);

  const siteIds = useMemo(() => {
    const drivers = Array.isArray(siteIdsData?.drivers) ? siteIdsData.drivers : [];
    const constructors = Array.isArray(siteIdsData?.constructors) ? siteIdsData.constructors : [];
    return { drivers, constructors };
  }, [siteIdsData]);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return expandedRows.filter((r) => {
      const typeOk = typeFilter === "ALL" ? true : r.type === typeFilter;
      const slotOk = slotFilter === "ALL" ? true : r.slot === slotFilter;
      const searchOk =
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        r.team.toLowerCase().includes(needle) ||
        String(r.start).includes(needle);
      return typeOk && slotOk && searchOk;
    });
  }, [expandedRows, q, typeFilter, slotFilter]);

  const [sort, setSort] = useState({ col: "proj", dir: "desc" });
  const usagePct = useMemo(() => {
    if (!lineups.length) return {};

    const counts = new Map();
    const allCounts = new Map();
    const denom = Math.max(1, lineups.length);

    for (const L of lineups) {
      const seenAll = new Set();

      if (L.cpt) {
        const k = makeRowKey("DRIVER", L.cpt, "CPT");
        counts.set(k, (counts.get(k) || 0) + 1);
        if (!seenAll.has(`DRIVER::${L.cpt}`)) {
          allCounts.set(`DRIVER::${L.cpt}`, (allCounts.get(`DRIVER::${L.cpt}`) || 0) + 1);
          seenAll.add(`DRIVER::${L.cpt}`);
        }
      }

      for (const n of L.flex || []) {
        const k = makeRowKey("DRIVER", n, "FLEX");
        counts.set(k, (counts.get(k) || 0) + 1);
        if (!seenAll.has(`DRIVER::${n}`)) {
          allCounts.set(`DRIVER::${n}`, (allCounts.get(`DRIVER::${n}`) || 0) + 1);
          seenAll.add(`DRIVER::${n}`);
        }
      }

      if (L.constructor) {
        const k = makeRowKey("CONSTRUCTOR", L.constructor, "CNSTR");
        counts.set(k, (counts.get(k) || 0) + 1);
        allCounts.set(`CONSTRUCTOR::${L.constructor}`, (allCounts.get(`CONSTRUCTOR::${L.constructor}`) || 0) + 1);
      }
    }

    const out = {};
    for (const [k, c] of counts.entries()) out[k] = (c / denom) * 100;
    for (const [k, c] of allCounts.entries()) out[k] = (c / denom) * 100;
    return out;
  }, [lineups]);

  const sortedRows = useMemo(() => {
    const mult = sort.dir === "asc" ? 1 : -1;
    const getVal = (r) => {
      if (sort.col === "usage") return usagePct[r.key] || 0;
      if (sort.col === "usage_all") return usagePct[`${r.type}::${r.name}`] || 0;
      if (sort.col === "slot") return r.slot;
      if (sort.col === "type") return r.type;
      if (sort.col === "team") return r.team;
      if (sort.col === "name") return r.name;
      if (sort.col === "start") return r.start || 0;
      if (sort.col === "salary") return r.salaryDisplay || 0;
      if (sort.col === "proj") return r.baseProjDisplay ?? r.projDisplay ?? 0;
      if (sort.col === "val") return r.baseValDisplay ?? r.valDisplay ?? 0;
      if (sort.col === "pown") return r.pownDisplay || 0;
      if (sort.col === "opt") return r.optDisplay || 0;
      if (sort.col === "floor") return r.floorDisplay || 0;
      if (sort.col === "ceil") return r.ceilDisplay || 0;
      return r.baseProjDisplay ?? r.projDisplay ?? 0;
    };

    return [...filteredRows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "string" || typeof vb === "string") {
        const sa = String(va || "");
        const sb = String(vb || "");
        if (sa < sb) return -1 * mult;
        if (sa > sb) return 1 * mult;
        return a.name.localeCompare(b.name) * mult;
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return a.name.localeCompare(b.name) * mult;
    });
  }, [filteredRows, sort, usagePct]);

  const setSortCol = (col) => {
    setSort((prev) => ({
      col,
      dir: prev.col === col ? (prev.dir === "asc" ? "desc" : "asc") : "desc",
    }));
  };
  const sortArrow = (k) => (sort.col === k ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const exposures = useMemo(() => {
    if (!lineups.length) return [];

    const denom = Math.max(1, lineups.length);

    if (expScope === "ALL") {
      const m = new Map();

      for (const L of lineups) {
        const seen = new Set();
        if (L.cpt && !seen.has(`DRIVER::${L.cpt}`)) {
          m.set(`DRIVER::${L.cpt}`, (m.get(`DRIVER::${L.cpt}`) || 0) + 1);
          seen.add(`DRIVER::${L.cpt}`);
        }
        for (const n of L.flex || []) {
          if (!seen.has(`DRIVER::${n}`)) {
            m.set(`DRIVER::${n}`, (m.get(`DRIVER::${n}`) || 0) + 1);
            seen.add(`DRIVER::${n}`);
          }
        }
        if (L.constructor) {
          m.set(`CONSTRUCTOR::${L.constructor}`, (m.get(`CONSTRUCTOR::${L.constructor}`) || 0) + 1);
        }
      }

      return [...m.entries()]
        .map(([k, count]) => {
          const [type, name] = k.split("::");
          return { name, slot: "ALL", type, count, pct: (count / denom) * 100 };
        })
        .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
    }

    const m = new Map();

    for (const L of lineups) {
      if (expScope === "CPT" && L.cpt) m.set(L.cpt, (m.get(L.cpt) || 0) + 1);
      if (expScope === "CNSTR" && L.constructor) m.set(L.constructor, (m.get(L.constructor) || 0) + 1);
      if (expScope === "FLEX") {
        for (const n of L.flex || []) m.set(n, (m.get(n) || 0) + 1);
      }
    }

    return [...m.entries()]
      .map(([name, count]) => ({
        name,
        slot: expScope,
        type: expScope === "CNSTR" ? "CONSTRUCTOR" : "DRIVER",
        count,
        pct: (count / denom) * 100,
      }))
      .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
  }, [lineups, expScope]);

  const resetConstraints = () => {
    setLocksArr([]);
    setExclsArr([]);
    setBoost({});
    setCustomProj({});
    setCustomProjDraft({});
    setMinPctTag({});
    setMaxPctTag({});
    setGroups([]);
    setLineups([]);
    setProgressActual(0);
    setProgressUI(0);
    setQ("");
    setSlotFilter("ALL");
    setTypeFilter("ALL");
    setExpScope("ALL");
    setOptBy("proj");
    setNumLineups(20);
    setMaxSalary(F1.cap);
    setGlobalMax(100);
    setMaxLineupPown("");
    setMaxGeoPown("");
    setRandomness(0);
  };

  const toggleLock = (row) => {
    setLocksArr((arr) => {
      const has = (arr || []).includes(row.key);
      return has ? arr.filter((x) => x !== row.key) : [...(arr || []), row.key];
    });
  };

  const toggleExcl = (row) => {
    setExclsArr((arr) => {
      const has = (arr || []).includes(row.key);
      return has ? arr.filter((x) => x !== row.key) : [...(arr || []), row.key];
    });
  };

  const bumpBoost = (name, step) => {
    setBoost((m) => ({ ...m, [name]: clamp((m[name] || 0) + step, -6, 6) }));
  };

  const setRowCustomProj = (rowKey, value) => {
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      if (value == null || String(value).trim() === "") delete next[rowKey];
      else next[rowKey] = String(value);
      return next;
    });
  };

  const resetRowCustomProj = (rowKey) => {
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      delete next[rowKey];
      return next;
    });
    setCustomProjDraft((m) => {
      const next = { ...(m || {}) };
      delete next[rowKey];
      return next;
    });
  };

  async function optimize() {
    if (!expandedRows.length) return;

    setLineups([]);
    setProgressActual(0);
    setProgressUI(0);
    setIsOptimizing(true);

    const payload = {
      site: "dk",
      cap: Math.min(F1.cap, Number(maxSalary) || F1.cap),
      n: Math.max(1, Number(numLineups) || 1),
      objective: optBy,
      randomness: clamp(Number(randomness) || 0, 0, 100),
      global_max_pct: clamp(Number(globalMax) || 100, 0, 100),
      lineup_pown_max:
        String(maxLineupPown).trim() === "" ? null : clamp(Number(maxLineupPown) || 0, 0, 600),
      geo_mean_pown_max:
        String(maxGeoPown).trim() === "" ? null : clamp(Number(maxGeoPown) || 0, 0, 100),
      locks: Array.from(locks),
      excludes: Array.from(excls),
      boosts: boost,
      min_pct_tag: Object.fromEntries(
        Object.entries(minPctTag || {}).map(([k, v]) => [k, clamp(Number(v) || 0, 0, 100)])
      ),
      max_pct_tag: Object.fromEntries(
        Object.entries(maxPctTag || {}).map(([k, v]) => [k, clamp(Number(v) || 100, 0, 100)])
      ),
      groups,
      drivers: driverRows
        .filter((r) => r.type === "DRIVER")
        .map((r) => ({
          name: r.name,
          slot: r.slot,
          team: r.team,
          start: r.start,
          salary: r.salaryDisplay,
          proj: r.projDisplay,
          floor: r.floorDisplay,
          ceil: r.ceilDisplay,
          pown: r.pownDisplay,
          opt: r.optDisplay,
          key: r.key,
        })),
      constructors: constructorRows.map((r) => ({
        name: r.name,
        slot: "CNSTR",
        team: r.team,
        salary: r.salaryDisplay,
        proj: r.projDisplay,
        floor: r.floorDisplay,
        ceil: r.ceilDisplay,
        pown: r.pownDisplay,
        opt: r.optDisplay,
        key: r.key,
      })),
      rules: {
        constructor_team_max_drivers: 1,
      },
    };

    try {
      const res = await fetch(`${API_BASE}/f1/solve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const j = await res.json();
        const out = (j.lineups || []).map((L) => ({
          cpt: L.cpt,
          flex: L.flex || [],
          constructor: L.constructor,
          salary: L.salary,
          total: L.total,
        }));
        setLineups(out);
        setProgressActual(out.length);
        setProgressUI(out.length);
        setIsOptimizing(false);
        clearInterval(tickRef.current);
        return;
      }
    } catch (_) {
      // fallback below
    }

    const out = localBuildF1Lineups({
      drivers: driverRows,
      constructors: constructorRows,
      objective: optBy,
      n: Math.max(1, Number(numLineups) || 1),
      cap: Math.min(F1.cap, Number(maxSalary) || F1.cap),
      randomnessPct: randomness,
      locks,
      excludes: excls,
      boostMap: boost,
      minPctTag,
      maxPctTag,
      globalMax,
      lineupPownCap:
        String(maxLineupPown).trim() === "" ? null : clamp(Number(maxLineupPown) || 0, 0, 600),
      geoMeanPownCap:
        String(maxGeoPown).trim() === "" ? null : clamp(Number(maxGeoPown) || 0, 0, 100),
      groups,
    });

    setLineups(out);
    setProgressActual(out.length);
    setProgressUI(out.length);
    setIsOptimizing(false);
    clearInterval(tickRef.current);
  }

  const getChosenForLineup = (L) => {
    const out = [];
    if (L.cpt) {
      const r = rowsBySlotAndName.get(`CPT::${L.cpt}`);
      if (r) out.push(r);
    }
    for (const n of L.flex || []) {
      const r = rowsBySlotAndName.get(`FLEX::${n}`);
      if (r) out.push(r);
    }
    if (L.constructor) {
      const r = rowsBySlotAndName.get(`CNSTR::${L.constructor}`);
      if (r) out.push(r);
    }
    return out;
  };

  const buildAnalysis = useMemo(() => {
    if (!lineups.length) {
      return {
        avgSalaryUsed: 0,
        avgProjection: 0,
        avgLineupPown: 0,
        avgGeoMeanPown: 0,
        topRows: [],
      };
    }

    const rowCounts = new Map();
    let totalSalary = 0;
    let totalProjection = 0;
    let totalPown = 0;
    let totalGeo = 0;

    for (const L of lineups) {
      const chosen = getChosenForLineup(L);
      totalSalary += Number(L.salary) || 0;
      totalProjection += Number(L.total) || 0;
      totalPown += chosen.reduce((s, r) => s + (r.pownDisplay || 0), 0);
      totalGeo += geoMeanPct(chosen.map((r) => r.pownDisplay || 0));

      if (L.cpt) rowCounts.set(makeRowKey("DRIVER", L.cpt, "CPT"), (rowCounts.get(makeRowKey("DRIVER", L.cpt, "CPT")) || 0) + 1);
      for (const n of L.flex || []) rowCounts.set(makeRowKey("DRIVER", n, "FLEX"), (rowCounts.get(makeRowKey("DRIVER", n, "FLEX")) || 0) + 1);
      if (L.constructor) rowCounts.set(makeRowKey("CONSTRUCTOR", L.constructor, "CNSTR"), (rowCounts.get(makeRowKey("CONSTRUCTOR", L.constructor, "CNSTR")) || 0) + 1);
    }

    const total = Math.max(1, lineups.length);
    const topRows = [...rowCounts.entries()]
      .map(([key, count]) => {
        const row = expandedRows.find((r) => r.key === key);
        return {
          key,
          name: row?.name || key,
          slot: row?.slot || "",
          count,
          pct: (count / total) * 100,
        };
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 15);

    return {
      avgSalaryUsed: totalSalary / total,
      avgProjection: totalProjection / total,
      avgLineupPown: totalPown / total,
      avgGeoMeanPown: totalGeo / total,
      topRows,
    };
  }, [lineups, expandedRows]);

    const lineupsForCards = useMemo(() => {
    const dir = cardsSortDir === "asc" ? 1 : -1;

    const getKey = (L) => {
      const chosen = getChosenForLineup(L);
      const totalPown = chosen.reduce((s, r) => s + (r.pownDisplay || 0), 0);
      const geoPown = geoMeanPct(chosen.map((r) => r.pownDisplay || 0));
      const totalOpt = chosen.reduce((s, r) => s + (r.optDisplay || 0), 0);

      if (cardsSortBy === "salary") return Number(L.salary) || 0;
      if (cardsSortBy === "pown") return totalPown || 0;
      if (cardsSortBy === "geo") return geoPown || 0;
      if (cardsSortBy === "opt") return totalOpt || 0;

      return Number(L.total) || 0;
    };

    return [...(lineups || [])].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  }, [lineups, cardsSortBy, cardsSortDir]);

  const header = "px-2 py-1 font-semibold text-center";
  const cell = "px-2 py-1 text-center";
  const inputClass = `w-full border rounded-md px-2 py-1.5 text-sm ${T.input}`;
  const tableInputClass =
    "w-16 border rounded px-1.5 py-0.5 text-center " +
    (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900");

  return (
    <div className={`${T.page} pb-8`}>
      <div className="w-full px-4 md:px-6 py-5">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <h1 className={`text-2xl md:text-3xl font-extrabold ${T.headerText}`}>Formula 1 — Optimizer</h1>
            {updatedAt && <div className={`text-sm ${T.subtext}`}>Updated: {fmtUpdated(updatedAt)}</div>}
          </div>

          <div className="flex flex-col items-end gap-3 self-end sm:self-auto">
          <div className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " + T.pill}>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={"px-3 py-1 rounded-full font-medium transition " + (isDark ? T.pillDarkBtnActive : T.pillDarkBtnIdle)}
            >
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={"px-3 py-1 rounded-full font-medium transition " + (!isDark ? T.pillLightBtnActive : T.pillLightBtnIdle)}
            >
              Light
            </button>
          </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs ${T.subtext}`}>Click here if new slate →</span>
              <button type="button" onClick={resetConstraints}
                className="h-8 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 active:bg-red-800">
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center">
          <div className={`inline-flex border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <button type="button" aria-pressed="true"
              className={`relative inline-flex items-center gap-2 pb-2 text-sm font-semibold transition ${isDark ? "text-white" : "text-slate-900"}`}>
              <img src="/logos/dk.png" alt="" className="w-4 h-4 object-contain" />
              <span>DraftKings</span>
              <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
            </button>
          </div>
        </div>

        <section className={`rounded-2xl p-3 mb-3 ${panelClass}`} aria-label="Build settings">
          <h2 className="text-sm font-semibold mb-3">Build Settings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 items-end">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Optimize by</label>
            <select className={inputClass} value={optBy} onChange={(e) => setOptBy(e.target.value)}>
              <option value="proj">Projection</option>
              <option value="floor">Floor</option>
              <option value="ceil">Ceiling</option>
              <option value="pown">pOWN%</option>
              <option value="opt">Opt%</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Lineups</label>
            <input className={inputClass} value={numLineups} onChange={(e) => setNumLineups(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max salary</label>
            <input className={inputClass} value={maxSalary} onChange={(e) => setMaxSalary(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Global Max %</label>
            <input className={inputClass} value={globalMax} onChange={(e) => setGlobalMax(e.target.value)} />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max lineup pOWN</label>
            <input className={inputClass} value={maxLineupPown} onChange={(e) => setMaxLineupPown(e.target.value)} placeholder="0–600" />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Max geo mean pOWN%</label>
            <input className={inputClass} value={maxGeoPown} onChange={(e) => setMaxGeoPown(e.target.value)} placeholder="e.g. 20–35" />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Randomness %</label>
            <input className={inputClass} value={randomness} onChange={(e) => setRandomness(e.target.value)} />
          </div>


        </div>

        </section>

        <details className={`rounded-2xl p-3 mb-3 ${panelClass}`}>
          <summary className="cursor-pointer select-none text-sm font-semibold">Build Rules</summary>
        <section className="pt-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-base font-semibold">Player Groups</h3>
            <button
              type="button"
              className={
                "px-3 py-1.5 rounded-md border text-sm transition " +
                (isDark ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-slate-800/70" : "bg-white border-slate-300 text-slate-900 hover:bg-gray-50")
              }
              onClick={() => setGroups((G) => [...(G || []), { mode: "at_most", count: 1, players: [] }])}
            >
              + Add group
            </button>
          </div>

          {(groups || []).length === 0 ? (
            <div className="text-sm text-gray-500">No groups yet. Click “Add group”.</div>
          ) : (
            <div className="space-y-3">
              {(groups || []).map((g, idx) => (
                <div key={idx} className="rounded-md border px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={g.mode || "at_most"}
                      onChange={(e) =>
                        setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, mode: e.target.value } : gg)))
                      }
                      className={inputClass}
                    >
                      <option value="at_most">At Most</option>
                      <option value="at_least">At Least</option>
                      <option value="exactly">Exactly</option>
                    </select>

                    <input
                      type="number"
                      min={0}
                      className={inputClass}
                      value={g.count ?? 1}
                      onChange={(e) =>
                        setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, count: Math.max(0, Number(e.target.value) || 0) } : gg)))
                      }
                    />

                    <button
                      type="button"
                      className={
                        "ml-auto px-2.5 py-1 rounded-md border text-sm transition " +
                        (isDark ? "bg-slate-900/60 border-slate-700 text-slate-100 hover:bg-red-900/40" : "bg-white border-slate-300 text-slate-900 hover:bg-red-50")
                      }
                      onClick={() => setGroups((Gs) => (Gs || []).filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  </div>

                  <DriverMultiPicker
                    allNames={allSelectableNames}
                    value={Array.isArray(g.players) ? g.players : []}
                    onChange={(players) => setGroups((Gs) => (Gs || []).map((gg, i) => (i === idx ? { ...gg, players } : gg)))}
                    placeholder="Type to search names and click to add…"
                    isDark={isDark}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        </details>

        <section className={`rounded-2xl p-3 mb-3 ${panelClass}`} aria-label="Optimize">
        <div className="flex flex-wrap items-center gap-3">
          <div className="shrink-0">
            <button className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold" onClick={optimize} type="button">
              Optimize {numLineups}
            </button>
          </div>
          <div className={`flex-1 min-w-[100px] max-w-[420px] h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-300"
              style={{
                width: `${(Math.min(progressUI, Math.max(1, Number(numLineups) || 1)) / Math.max(1, Number(numLineups) || 1)) * 100}%`,
              }}
            />
          </div>
          <div className="text-sm text-gray-500 min-w-[60px] text-right">
            {progressUI}/{numLineups}
          </div>
        </div>

          <details className={`mt-3 border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={
                "px-3 py-1.5 text-xs md:text-sm rounded-lg border transition font-medium " +
                (isDark ? "bg-red-900/30 border-red-700/60 text-red-200 hover:bg-red-900/45 hover:text-red-100" : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100")
              }
              onClick={resetConstraints}
            >
              Reset All
            </button>

            <button className={"px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")} onClick={() => setLocksArr([])} type="button">
              Clear locks
            </button>

            <button className={"px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")} onClick={() => setExclsArr([])} type="button">
              Clear excludes
            </button>

            <button className={"px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")} onClick={() => { setMinPctTag({}); setMaxPctTag({}); }} type="button">
              Clear exposures
            </button>

            <button className={"px-2.5 py-1.5 text-xs md:text-sm rounded-lg border transition " + (isDark ? "bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-300 text-slate-700 hover:bg-gray-100")} onClick={() => setBoost({})} type="button">
              Clear boosts
            </button>
          </div>
          </details>
        </section>

        <section className={`w-full rounded-2xl overflow-hidden ${panelClass}`} aria-label="Player pool">
          <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-semibold">Player Pool</h2>
              <span className={`text-xs ${T.subtext}`}>{sortedRows.length.toLocaleString()} entries shown</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <button type="button" aria-pressed="true" title="Driver position" className="pb-2 border-b-2 border-sky-400 text-sky-500 text-xs font-semibold">D</button>
              <div className="w-full sm:w-auto sm:ml-auto">
          <input
            className={
              "border rounded-md px-3 py-1.5 w-full sm:w-80 text-sm transition " +
              (isDark ? "bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-500")
            }
            placeholder="Search name/team..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

              </div>
            </div>
        <div className="mb-1 flex flex-wrap items-baseline gap-x-6 gap-y-3">
          <div className="inline-flex items-baseline gap-3">
          <span className={`text-xs ${T.subtext}`}>Type:</span>
          <div className="inline-flex items-baseline gap-3">
            {["ALL", "DRIVER", "CONSTRUCTOR"].map((t) => {
              const active = typeFilter === t;
              return (
                <button key={t} type="button" onClick={() => setTypeFilter(t)} className={plainTabClass(active)}>
                  {t === "ALL" ? "All" : t === "DRIVER" ? "Drivers" : "Constructors"}
                </button>
              );
            })}
          </div>

          </div>
          <div className="inline-flex items-baseline gap-3">
          <span className={`text-xs ${T.subtext}`}>Slot:</span>
          <div className="inline-flex items-baseline gap-3">
            {["ALL", "CPT", "FLEX", "CNSTR"].map((t) => {
              const active = slotFilter === t;
              return (
                <button key={t} type="button" onClick={() => setSlotFilter(t)} className={plainTabClass(active)}>
                  {t}
                </button>
              );
            })}
          </div>

          </div>
        </div>
          </div>
          <div className="max-h-[520px] overflow-auto">
            <table className={`w-full border-separate text-[12px] ${isDark ? "text-slate-100" : "text-slate-900"}`} style={{ borderSpacing: 0 }}>
              <thead className={`${isDark ? "bg-slate-900" : "bg-gray-50"} sticky top-0 z-20 shadow-sm`}>
                <tr>
                  <th className={header}>Lock</th>
                  <th className={header}>Excl</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("type")}>Type{sortArrow("type")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("slot")}>Slot{sortArrow("slot")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("team")}>Team{sortArrow("team")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("start")}>Start{sortArrow("start")}</th>
                  <th className={header}>Boosts</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("name")}>Name{sortArrow("name")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("salary")}>Salary{sortArrow("salary")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("proj")}>Proj{sortArrow("proj")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("floor")}>Floor{sortArrow("floor")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("ceil")}>Ceiling{sortArrow("ceil")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("val")}>Val{sortArrow("val")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("pown")}>pOWN%{sortArrow("pown")}</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("opt")}>Opt%{sortArrow("opt")}</th>
                  <th className={header}>Min%</th>
                  <th className={header}>Max%</th>
                  <th className={header + " cursor-pointer"} onClick={() => setSortCol("usage")}>Usage%{sortArrow("usage")}</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cell} text-gray-500`} colSpan={18}>
                      Loading...
                    </td>
                  </tr>
                )}

                {error && (
                  <tr>
                    <td className={`${cell} text-red-500`} colSpan={18}>
                      Failed to load data.
                    </td>
                  </tr>
                )}

                {!loading && !error && sortedRows.map((r) => (
                  <tr
                    key={r.key}
                    className={
                      "transition-colors " +
                      (isDark ? "odd:bg-slate-950/70 even:bg-slate-900/60 hover:bg-slate-800/80" : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/60")
                    }
                  >
                    <td className={cell}>
                      <input type="checkbox" checked={locks.has(r.key)} onChange={() => toggleLock(r)} />
                    </td>

                    <td className={cell}>
                      <input type="checkbox" checked={excls.has(r.key)} onChange={() => toggleExcl(r)} />
                    </td>

                    <td className={cell}>{r.type === "DRIVER" ? "Driver" : "Constructor"}</td>
                    <td className={cell}>{r.slot}</td>
                    <td className={cell}>
                      <span className="px-1.5 py-0.5 rounded text-[10px] leading-none font-semibold inline-flex items-center justify-center" style={teamPillClass(r.team)}>
                        {r.team}
                      </span>
                    </td>
                    <td className={cell}>{r.type === "DRIVER" ? fmt0(r.start) : "—"}</td>

                    <td className={cell}>
                      <div className="inline-flex items-center gap-1">
                        <button className={`inline-flex items-center justify-center h-[18px] w-[18px] p-0 rounded border text-[8px] leading-none transition ${isDark ? "border-slate-700 text-slate-400 hover:text-emerald-300 hover:bg-slate-800" : "border-slate-300 text-slate-500 hover:text-emerald-700 hover:bg-slate-100"}`} title="+3%" onClick={() => bumpBoost(r.name, +1)} type="button">
                          ▲
                        </button>
                        <span className="w-4 text-center text-[11px] tabular-nums">{boost[r.name] || 0}</span>
                        <button className={`inline-flex items-center justify-center h-[18px] w-[18px] p-0 rounded border text-[8px] leading-none transition ${isDark ? "border-slate-700 text-slate-400 hover:text-rose-300 hover:bg-slate-800" : "border-slate-300 text-slate-500 hover:text-rose-700 hover:bg-slate-100"}`} title="-3%" onClick={() => bumpBoost(r.name, -1)} type="button">
                          ▼
                        </button>
                      </div>
                    </td>

                    <td className={cell}>{r.name}</td>
                    <td className={cell}>{fmt0(r.salaryDisplay)}</td>
                    <td className={cell}>
                      {(() => {
                        const draftKey = r.key;
                        const rawCustom = customProj?.[r.key] ?? "";
                        const hasCustom = rawCustom != null && String(rawCustom).trim() !== "";
                        const baseProj = Number(r.baseProjDisplay ?? r.projDisplay ?? 0) || 0;
                        const activeProj = hasCustom ? Math.max(0, num(rawCustom)) : baseProj;
                        const delta = activeProj - baseProj;
                        const tone = hasCustom && Math.abs(delta) > 0.0001 ? (delta > 0 ? "up" : "down") : "flat";

                        const projInputValue = Object.prototype.hasOwnProperty.call(customProjDraft, draftKey)
                          ? customProjDraft[draftKey]
                          : fmt1(activeProj);

                        const projInputStyle =
                          tone === "up"
                            ? {
                                color: "#22c55e",
                                borderColor: "#22c55e",
                                backgroundColor: isDark ? "rgba(34,197,94,0.10)" : "rgba(34,197,94,0.08)",
                              }
                            : tone === "down"
                            ? {
                                color: "#ef4444",
                                borderColor: "#ef4444",
                                backgroundColor: isDark ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.08)",
                              }
                            : {};

                        return (
                          <div className="inline-flex items-center justify-center gap-1">
                            <input
                              type="number"
                              step="0.1"
                              inputMode="decimal"
                              className={tableInputClass + " font-semibold tabular-nums"}
                              value={projInputValue}
                              style={projInputStyle}
                              onFocus={() => {
                                setCustomProjDraft((m) => {
                                  if (Object.prototype.hasOwnProperty.call(m, draftKey)) return m;
                                  return { ...(m || {}), [draftKey]: fmt1(activeProj) };
                                });
                              }}
                              onBlur={() => {
                                setCustomProjDraft((m) => {
                                  const next = { ...(m || {}) };
                                  delete next[draftKey];
                                  return next;
                                });
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;

                                setCustomProjDraft((m) => ({
                                  ...(m || {}),
                                  [draftKey]: raw,
                                }));

                                if (raw == null || String(raw).trim() === "") {
                                  resetRowCustomProj(r.key);
                                  return;
                                }

                                const nextProj = Number(raw);
                                if (Number.isFinite(nextProj)) {
                                  setRowCustomProj(r.key, String(Math.max(0, nextProj)));
                                }
                              }}
                              title={
                                hasCustom
                                  ? `Custom projection. Original: ${fmt1(baseProj)} | Current: ${fmt1(activeProj)}`
                                  : "Use arrows or type to edit projection"
                              }
                            />
                            {hasCustom ? (
                              <button
                                type="button"
                                className={
                                  "rounded border px-1 py-0.5 text-[10px] leading-none " +
                                  (isDark
                                    ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                                    : "border-slate-300 text-slate-600 hover:bg-slate-100")
                                }
                                title="Reset projection"
                                onClick={() => resetRowCustomProj(r.key)}
                              >
                                ↺
                              </button>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className={cell}>{fmt1(r.floorDisplay)}</td>
                    <td className={cell}>{fmt1(r.ceilDisplay)}</td>
                    <td className={cell}>{fmt1(r.valDisplay)}</td>
                    <td className={cell}>{fmt1(r.pownDisplay)}</td>
                    <td className={cell}>{fmt1(r.optDisplay)}</td>

                    <td className={cell}>
                      <input
                        className={tableInputClass}
                        value={minPctTag[r.key] ?? ""}
                        onChange={(e) =>
                          setMinPctTag((m) => ({
                            ...m,
                            [r.key]: e.target.value === "" ? "" : clamp(num(e.target.value), 0, 100),
                          }))
                        }
                        placeholder="—"
                      />
                    </td>

                    <td className={cell}>
                      <input
                        className={tableInputClass}
                        value={maxPctTag[r.key] ?? ""}
                        onChange={(e) =>
                          setMaxPctTag((m) => ({
                            ...m,
                            [r.key]: e.target.value === "" ? "" : clamp(num(e.target.value), 0, 100),
                          }))
                        }
                        placeholder="—"
                      />
                    </td>

                    <td className={cell}>{usagePct[r.key] != null ? fmt1(usagePct[r.key]) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {!!lineups.length && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <section className={`lg:col-span-8 rounded-lg p-3 ${T.card}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h2 className="text-base font-bold">Lineups ({lineups.length})</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    className={actionBtnClass}
                    onClick={() =>
                      downloadDKEntriesCSV({
                        lineups,
                        driverSiteIds: siteIds.drivers,
                        constructorSiteIds: siteIds.constructors,
                        fname: "f1_dk_entries.csv",
                      })
                    }
                    type="button"
                  >
                    Export CSV (DK IDs)
                  </button>
                </div>
              </div>

              <div className="overflow-auto max-h-[420px]">
                <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
                  <thead className={(isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900") + " sticky top-0 z-20 shadow-sm"}>
                    <tr>
                      <th className={header}>#</th>
                      <th className={header}>Salary</th>
                      <th className={header}>Total {optBy === "pown" ? "pOWN%" : optBy === "opt" ? "Opt%" : "Proj"}</th>
                      <th className={header}>Total pOWN%</th>
                      <th className={header}>Lineup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineups.map((L, i) => {
                      const chosen = getChosenForLineup(L);
                      const totalPown = chosen.reduce((s, r) => s + (r.pownDisplay || 0), 0);
                      return (
                        <tr key={i} className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}>
                          <td className={cell}>{i + 1}</td>
                          <td className={cell}>{fmt0(L.salary)}</td>
                          <td className={cell}>{fmt1(L.total)}</td>
                          <td className={cell}>{fmt1(totalPown)}</td>
                          <td className="px-2 py-1 leading-snug text-left">
                            <span className="font-semibold">CPT:</span> {L.cpt}
                            <span className="mx-2">•</span>
                            <span className="font-semibold">FLEX:</span> {(L.flex || []).join(" • ")}
                            <span className="mx-2">•</span>
                            <span className="font-semibold">CNSTR:</span> {L.constructor}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={`lg:col-span-4 rounded-lg p-3 ${T.card}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className={`text-base font-semibold ${T.headerText}`}>Exposure</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex flex-wrap items-center gap-3">
                    {["ALL", "CPT", "FLEX", "CNSTR"].map((t) => {
                      const active = expScope === t;
                      return (
                        <button key={t} type="button" onClick={() => setExpScope(t)} className={plainTabClass(active)}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  <button className={actionBtnClass} onClick={() => downloadExposuresCSV(exposures)} type="button">
                    Export
                  </button>
                </div>
              </div>

              <div className="overflow-auto max-h-[420px]">
                <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                  <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                    <tr>
                      <th className={header}>Name</th>
                      <th className={header}>Slot</th>
                      <th className={header}>Count</th>
                      <th className={header}>Exposure %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exposures.map((r) => (
                      <tr key={`${r.slot}-${r.name}`} className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}>
                        <td className={cell}>{r.name}</td>
                        <td className={cell}>{r.slot}</td>
                        <td className={cell}>{fmt0(r.count)}</td>
                        <td className={cell}>{fmt1(r.pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <details className={`lg:col-span-12 rounded-2xl p-3 ${panelClass}`}>
              <summary className={`cursor-pointer select-none text-sm font-semibold ${T.headerText}`}>Build Analysis</summary>

              <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className={`rounded-lg p-3 ${T.card}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${T.headerText}`}>Summary</h4>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Used</div>
                    <div className="text-right tabular-nums">{fmt0(buildAnalysis.avgSalaryUsed)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Projection</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgProjection)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Lineup pOWN%</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgLineupPown)}</div>

                    <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Geo Mean pOWN%</div>
                    <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgGeoMeanPown)}</div>
                  </div>
                </div>

                <div className={`rounded-lg p-3 xl:col-span-2 ${T.card}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${T.headerText}`}>Top Slot Rows</h4>
                  <div className="overflow-auto max-h-[260px]">
                    <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
                      <thead className={`${isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"} sticky top-0 z-20 shadow-sm`}>
                        <tr>
                          <th className={header}>Name</th>
                          <th className={header}>Slot</th>
                          <th className={header}>Count</th>
                          <th className={header}>Build %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildAnalysis.topRows.map((r) => (
                          <tr key={r.key} className={isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50"}>
                            <td className={cell}>{r.name}</td>
                            <td className={cell}>{r.slot}</td>
                            <td className={cell}>{fmt0(r.count)}</td>
                            <td className={cell}>{fmt1(r.pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </details>

            <section className="lg:col-span-12">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <h3 className="text-base font-semibold">Cards</h3>

                <div className="flex flex-wrap items-center gap-3">
                  <span className={isDark ? "text-slate-400 text-sm" : "text-gray-600 text-sm"}>
                    Sort lineups by:
                  </span>

                  <select
                    className={`h-8 border rounded-md px-2 py-0 text-xs ${T.input}`}
                    value={cardsSortBy}
                    onChange={(e) => setCardsSortBy(e.target.value)}
                  >
                    <option value="proj">Projection</option>
                    <option value="salary">Salary</option>
                    <option value="pown">pOWN%</option>
                    <option value="geo">Geo Mean%</option>
                    <option value="opt">Opt%</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => setCardsSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    className={actionBtnClass}
                    title="Toggle sort direction"
                  >
                    {cardsSortDir === "asc" ? "Asc ▲" : "Desc ▼"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 min-w-0">
                {lineupsForCards.map((L, idx) => {
                  const chosen = getChosenForLineup(L);

                  const slotOrder = { CPT: 0, FLEX: 1, CNSTR: 2 };

                  const chosenSorted = chosen.length
                    ? [...chosen].sort((a, b) => {
                        const sa = slotOrder[a.slot] ?? 99;
                        const sb = slotOrder[b.slot] ?? 99;
                        if (sa !== sb) return sa - sb;

                        if (a.slot === "FLEX" && b.slot === "FLEX") {
                          if ((b.salaryDisplay || 0) !== (a.salaryDisplay || 0)) {
                            return (b.salaryDisplay || 0) - (a.salaryDisplay || 0);
                          }
                        }

                        return String(a.name || "").localeCompare(String(b.name || ""));
                      })
                    : [];

                  const totalPown = chosenSorted.reduce((s, r) => s + (r.pownDisplay || 0), 0);
                  const totalOpt = chosenSorted.reduce((s, r) => s + (r.optDisplay || 0), 0);
                  const geoPown = geoMeanPct(chosenSorted.map((r) => r.pownDisplay || 0));

                  const cardCell = "px-1 py-1 text-center text-[10px] sm:text-[11px]";
                  const cardHeader = "px-1 py-1 font-semibold text-center text-[8px] sm:text-[10px]";
                  const zebraCell = cardCell + (isDark ? " bg-slate-900/40" : "");
                  const crown =
                    idx === 0 && cardsSortDir === "desc"
                      ? " 👑"
                      : "";

                  return (
                    <div key={idx} className={`rounded-2xl p-3 min-w-0 ${panelClass}`} data-lineup-card="true">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="font-semibold">Lineup #{idx + 1}</div>

                        <div className="flex items-center gap-1.5">
                          <span
                            className={
                              "text-[10px] font-medium tracking-wide " +
                              (isDark ? "text-slate-400" : "text-slate-500")
                            }
                            title="Geometric Mean pOWN%"
                          >
                            Geo Mean% {fmt1(geoPown)}{crown}
                          </span>
                          <img
                            src="/logos/dk.png"
                            alt="DraftKings"
                            title="DraftKings"
                            className="w-5 h-5 object-contain shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </div>
                      </div>

                      <div className="w-full min-w-0">
                        <table className="w-full table-fixed text-[12px]">
                          <thead>
                            <tr className={isDark ? "text-slate-400" : "text-gray-600"}>
                              <th className={`${cardHeader} w-[13%]`}> Slot</th>
                              <th className={`${cardHeader} w-[27%]`}> Name</th>
                              <th className={`${cardHeader} w-[11%]`}> Team</th>
                              <th className={`${cardHeader} w-[9%]`}> Start</th>
                              <th className={`${cardHeader} w-[12%]`}> pOWN%</th>
                              <th className={`${cardHeader} w-[12%]`}> Proj</th>
                              <th className={`${cardHeader} w-[16%]`}> Salary</th>
                            </tr>
                          </thead>

                        <tbody>
                          {chosenSorted.map((r, i) => (
                            <tr
                              key={`${r.key}-${i}`}
                              className={
                                "transition-colors " +
                                (isDark
                                  ? i % 2 === 0
                                    ? "bg-slate-950/70"
                                    : "bg-slate-900/60 hover:bg-slate-800/80"
                                  : i % 2 === 0
                                  ? "bg-white"
                                  : "bg-slate-100 hover:bg-blue-50/70")
                              }
                            >
                              <td className={`${zebraCell} font-semibold`}>
                                {r.slot}
                              </td>
                              <td
                                className={`${zebraCell} text-left break-words`}
                                title={r.name || ""}
                              >
                                {r.type === "DRIVER" ? shortDriverName(r.name) : r.name}
                              </td>
                              <td className={zebraCell}>
                                {r.team || "—"}
                              </td>
                              <td className={`${zebraCell} tabular-nums`}>
                                {r.type === "DRIVER" ? fmt0(r.start) : "—"}
                              </td>
                              <td className={`${zebraCell} tabular-nums`}>
                                {fmt1(r.pownDisplay || 0)}
                              </td>
                              <td className={`${zebraCell} tabular-nums`}>
                                {fmt1(r.projDisplay || 0)}
                              </td>
                              <td className={`${zebraCell} tabular-nums`}>
                                {fmt0(r.salaryDisplay || 0)}
                              </td>
                            </tr>
                          ))}

                          <tr className={isDark ? "border-t border-slate-700" : "border-t"}>
                            <td className={`${cardCell} font-semibold`}>Totals</td>
                            <td className={cardCell} />
                            <td className={cardCell} />
                            <td className={cardCell} />
                            <td className={`${cardCell} tabular-nums font-semibold`}>
                              {fmt1(totalPown)}
                            </td>
                            <td className={`${cardCell} tabular-nums font-semibold`}>
                              {fmt1(L.total)}
                            </td>
                            <td className={`${cardCell} tabular-nums font-semibold`}>
                              {fmt0(L.salary)}
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className={`mt-2 text-[11px] ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Total Opt% {fmt1(totalOpt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}